import React, { useRef, useEffect } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  animate,
} from 'framer-motion';
import {
  Wifi, Shield, Lightbulb, Thermometer, Lock, Cpu, Zap, Wind, Volume2, Smartphone,
  Home, Clock, Building2,
} from 'lucide-react';
import { useMouseContext } from '../MouseContext';

/* ─── Brand copy ─────────── */
const HEADLINE_WORDS = ['Intelligence', 'Powered', 'Smart Automation.'];

const SPR_CFG = { stiffness: 45, damping: 30, mass: 1.2 };
const SPR_MOUSE = { stiffness: 60, damping: 25, mass: 0.6 };

/* ─── Static Icons data (x=left%, y=viewport% matching scan range) ── */
const STATIC_ICONS = [
  { x: '10%', y: 8,  Icon: Wifi,         label: 'Mesh Network' },
  { x: '82%', y: 18, Icon: Shield,        label: 'Security' },
  { x: '30%', y: 28, Icon: Lightbulb,     label: 'Smart Lighting' },
  { x: '68%', y: 38, Icon: Thermometer,   label: 'Climate' },
  { x: '14%', y: 50, Icon: Lock,          label: 'Access Control' },
  { x: '78%', y: 58, Icon: Cpu,           label: 'Control Core' },
  { x: '42%', y: 68, Icon: Zap,           label: 'Energy' },
  { x: '22%', y: 78, Icon: Wind,          label: 'HVAC' },
  { x: '72%', y: 86, Icon: Volume2,       label: 'Audio' },
  { x: '52%', y: 94, Icon: Smartphone,    label: 'Mobile App' },
];

/* ─── Individual floating icon ────────────────────────── */
function FloatingIcon({ x, y, Icon, label }) {
  return (
    <motion.div
      className="absolute flex flex-col items-center gap-2 pointer-events-none select-none"
      style={{ left: x, top: `${y}%`, x: '-50%', y: '-50%', opacity: 0.35 }}
    >
      {/* Bubble — matches AntiGravityHero glass pill, beige-adapted */}
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center relative overflow-hidden backdrop-blur-md"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(201,168,76,0.08) 100%)',
          border: '1px solid rgba(201,168,76,0.28)',
          boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.6), 0 10px 30px rgba(201,168,76,0.18)',
        }}
      >
        <div
          className="absolute top-2 left-3 w-4 h-2 rounded-full"
          style={{ background: 'rgba(255,255,255,0.65)', filter: 'blur(1px)', transform: 'rotate(-15deg)' }}
        />
        <div style={{ color: 'rgba(139,100,20,0.9)', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 0 4px rgba(201,168,76,0.4))' }}>
          <Icon size={18} strokeWidth={1.5} />
        </div>
      </div>
    </motion.div>
  );
}

export default function LogoIntro() {
  const containerRef = useRef(null);
  const { smoothNormX, smoothNormY } = useMouseContext();


  /* ── Scroll progress ── */
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  /* ── LAYER 1: Far Background (Atmosphere) ── */
  const farY = useTransform(scrollYProgress, [0, 1], ['0vh', '15vh']);
  const farOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);

  /* ── LAYER 2: Midground (Grid) ── */
  const gridYScroll = useTransform(scrollYProgress, [0, 1], ['0px', '300px']);
  const gridOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.2]);

  /* ── LAYER 3: Near (Accents) ── */
  const accentRotate = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const accentYScroll = useTransform(scrollYProgress, [0, 1], ['0vh', '-20vh']);

  /* ── LAYER 4: UI Sequence ── */
  // 1. Logo
  const logoNum = useTransform(scrollYProgress, [0, 0.3], [0, -42]);
  const logoScaleNum = useTransform(scrollYProgress, [0, 0.3], [1, 0.35]);
  
  const logoSpring = useSpring(logoNum, SPR_CFG);
  const logoScale = useSpring(logoScaleNum, SPR_CFG);
  const logoY = useTransform(logoSpring, v => `${v}vh`);

  // 2. Text Y position
  const textNum = useTransform(scrollYProgress, [0, 0.35, 0.85], [20, 0, -120]);
  const textSpring = useSpring(textNum, SPR_CFG);
  const textY = useTransform(textSpring, v => `${v}vh`);

  // 3. Text Opacity
  const textOpacityRaw = useTransform(scrollYProgress, [0, 0.1, 0.35, 0.6, 0.85], [0, 0, 1, 1, 0]);
  const textOpacity = useSpring(textOpacityRaw, SPR_CFG);

  // 4. Big Logo Opacity (fades out as Header A appears)
  const logoOpacityRaw = useTransform(scrollYProgress, [0.25, 0.35], [1, 0]);
  const logoOpacity = useSpring(logoOpacityRaw, SPR_CFG);

  /* ── Mouse transforms ── */
  const logoRotateY = useTransform(smoothNormX, [-1, 1], [-12, 12]);
  const logoRotateX = useTransform(smoothNormY, [-1, 1], [8, -8]);
  const smoothLogoRotateY = useSpring(logoRotateY, SPR_MOUSE);
  const smoothLogoRotateX = useSpring(logoRotateX, SPR_MOUSE);

  const accent1X = useTransform(smoothNormX, [-1, 1], [-20, 20]);
  const accent1Y = useTransform(smoothNormY, [-1, 1], [-20, 20]);
  const accent2X = useTransform(smoothNormX, [-1, 1], [20, -20]);
  const accent2Y = useTransform(smoothNormY, [-1, 1], [20, -20]);
  const accent2Rotate = useSpring(useTransform(smoothNormX, [-1, 1], [-20, 20]), SPR_MOUSE);

  return (
    <section ref={containerRef} className="relative" style={{ height: '100vh' }}>
      <div
        className="sticky top-0 h-screen w-full overflow-hidden"
        style={{ background: 'var(--bg-primary)', perspective: '1200px' }}
      >
        {/* ── LAYER 1: FAR BACKGROUND ── */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-[-1] will-change-transform"
          style={{ y: farY, opacity: farOpacity }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-15"
               style={{ background: 'radial-gradient(circle, var(--gold) 0%, transparent 70%)', filter: 'blur(120px)' }} />
          <div className="absolute bottom-0 left-0 w-full h-[35vh] opacity-[0.04] flex items-end">
             <div className="w-1/4 h-[85%] bg-gold mr-1" />
             <div className="w-1/6 h-[65%] bg-gold mr-1" />
             <div className="w-1/3 h-[95%] bg-gold mr-1" />
             <div className="w-1/4 h-[75%] bg-gold" />
          </div>
        </motion.div>

        {/* ── LAYER 2: MIDGROUND GRID + SCAN LINE + ICONS ── */}
        <motion.div
          className="absolute inset-0 pointer-events-none will-change-transform overflow-hidden"
          style={{ y: gridYScroll, opacity: gridOpacity }}
        >
          {/* Line grid */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(201,168,76,0.10) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(201,168,76,0.10) 1px, transparent 1px)
              `,
              backgroundSize: '64px 64px',
            }}
          />

          {/* Floating Smart-home icons */}
          {STATIC_ICONS.map(({ x, y, Icon, label }, i) => (
            <FloatingIcon key={label} x={x} y={y} Icon={Icon} label={label} index={i} />
          ))}
        </motion.div>

        {/* ── LAYER 3: NEAR ACCENTS ── */}
        <motion.div
          className="absolute top-[15%] right-[12%] w-40 h-40 rounded-full pointer-events-none border border-gold/10 will-change-transform"
          style={{
            x: accent1X,
            y: accent1Y,
            rotate: accentRotate,
          }}
        />
        <motion.div
          className="absolute bottom-[15%] left-[8%] w-56 h-56 rounded-[4rem] pointer-events-none border border-gold/5 will-change-transform"
          style={{
            x: accent2X,
            y: accent2Y,
            rotate: accent2Rotate,
          }}
        />

        {/* ── LAYER 4: UI COMPONENTS (LOCKED) ── */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-50"
          style={{
            rotateY: smoothLogoRotateY,
            rotateX: smoothLogoRotateX,
          }}
        >
          <motion.div
            style={{
              y: logoY,
              scale: logoScale,
              opacity: logoOpacity,
            }}
            className="flex flex-col items-center gap-4 origin-top"
          >
            <motion.img
              src="/iotiq-logo.png"
              alt="IOTIQ"
              className="w-[60vw] max-w-md object-contain"
              style={{ filter: 'drop-shadow(0 0 50px rgba(201,168,76,0.2))' }}
            />
          </motion.div>
        </motion.div>

        {/* TEXT SEQUENCE */}
        <motion.div 
          className="absolute inset-0 flex flex-col items-center justify-start pointer-events-none pt-[45vh] z-40"
          style={{ y: textY, opacity: textOpacity }}
        >
          <div className="text-center mb-2">
            {HEADLINE_WORDS.map((word, i) => (
              <h1
                key={i}
                className="block text-4xl sm:text-6xl md:text-8xl font-serif font-light tracking-tight leading-[1.1]"
                style={{ color: i === 2 ? 'var(--iotiq-green)' : 'var(--text-primary)' }}
              >
                {word}
              </h1>
            ))}
          </div>

          <div className="text-center">
            <div className="w-12 h-px mx-auto mb-8 bg-gold/20" />
            <p className="max-w-[45ch] leading-relaxed text-2xl md:text-3xl text-theme-secondary mx-auto">
              1000+ Projects Delivered.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
